/**
 * What a module is, as tags under its playlist.
 *
 * The card used to lead with a sentence —
 *
 * > **6 capsules of Arabic for 1st year — 18 coins to win 🪙**
 *
 * — and the sentence was in the wrong place doing the wrong job. Wrong place: it
 * sat *above* the strip, between the post and the thing it was describing, so the
 * first thing a reader met was arithmetic about a playlist they had not seen. Wrong
 * job: it introduced the module, and the module's own title and description already
 * do that (see `ModuleFeedEmbed`). Read twice, the card said *here is a course, and
 * here is a sentence restating the course*.
 *
 * So the same facts come back as tags, under the strip, where a reader has just
 * counted the capsules and wants the two things the tiles cannot tell them: which
 * subject and which year. Three deliberate choices:
 *
 *  - **No capsule count.** It is one tap above, drawn six times, and a chip that
 *    says *6 capsules* beside six tiles is the count a reader can already see.
 *  - **The order is the order a reader asks.** What it is (Arabic), then who it is
 *    for (1st year), then what it pays (18 coins) — the same order the sentence
 *    used, because the order was never the problem.
 *  - **The coins keep the accent.** A tag is a label, and this one label is the
 *    reason to press the button below it, so it is the one chip in the app's blue
 *    (see `Chip`).
 *
 * A field the record did not state is left out rather than printed empty, exactly
 * as the sentence and the chip row both did: a module written before a field
 * existed simply has fewer tags.
 */
import {View} from 'react-native'
import {Plural, useLingui} from '@lingui/react/macro'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {atoms as a} from '#/alf'
import {Chip} from './ModuleFactsRow'

export function ModuleFactsTags({
  level,
  subject,
  coins,
  testID,
}: {
  /** The grade code, `Level_A5`, when the record states one. */
  level: string | null
  /** The subject code, `Maths`, when the record states one. */
  subject: string | null
  /** Every exercise in every capsule. */
  coins: number | null
  testID?: string
}) {
  const {t: l} = useLingui()

  const levelName = gradeLabel(level)
  const subjectName = subjectLabel(subject)

  return (
    <View
      testID={testID}
      style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
      {subjectName ? <Chip label={l(subjectName)} /> : null}
      {levelName ? <Chip label={l(levelName)} /> : null}
      {coins != null && coins > 0 ? (
        <Chip
          reward
          label={
            <Plural value={coins} one="# coin to win" other="# coins to win" />
          }
        />
      ) : null}
    </View>
  )
}
